import React, { useEffect } from 'react';
import { AnimationState } from '../types';

interface AnimationControlsProps {
  animation: AnimationState;
  timestampString: string;
  timeRange: string;
  planeCount: number;
  timeframeInfo?: { start: number; end: number; planeId: string } | null;
  onPlay: () => void;
  onPause: () => void;
  onReset: () => void;
  onSpeedChange: (speed: number) => void;
  onTimelineChange: (timestamp: number) => void;
  onStepForward: () => void;
  onStepBackward: () => void;
}

const AnimationControls: React.FC<AnimationControlsProps> = ({
  animation,
  timestampString,
  timeRange,
  planeCount,
  timeframeInfo,
  onPlay,
  onPause,
  onReset,
  onSpeedChange,
  onTimelineChange,
  onStepForward,
  onStepBackward,
}) => {
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          animation.isPlaying ? onPause() : onPlay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          onStepForward();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onStepBackward();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [animation.isPlaying, onPlay, onPause, onStepForward, onStepBackward]);

  return (
    <div className="animation-controls">
      <div className="anim-left">
        <button className="anim-btn" onClick={onReset} title="Reset (R)">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>

        <button className="anim-btn" onClick={onStepBackward} title="Step back (←)">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="19 21 9 12 19 3 19 21" />
            <rect x="5" y="3" width="2" height="18" />
          </svg>
        </button>

        {animation.isPlaying ? (
          <button className="anim-btn pause" onClick={onPause} title="Pause (Space)">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          </button>
        ) : (
          <button className="anim-btn play" onClick={onPlay} title="Play (Space)">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        )}

        <button className="anim-btn" onClick={onStepForward} title="Step forward (→)">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 15 12 5 21 5 3" />
            <rect x="17" y="3" width="2" height="18" />
          </svg>
        </button>

        <div className="anim-speed">
          <select
            value={animation.speed}
            onChange={(e) => onSpeedChange(Number(e.target.value))}
          >
            <option value="0.5">0.5x</option>
            <option value="1">1x</option>
            <option value="2">2x</option>
            <option value="3">3x</option>
            <option value="5">5x</option>
          </select>
        </div>
      </div>

      <div className="anim-center">
        <div className="anim-time-value">{timestampString}</div>
        <input
          type="range"
          min="1"
          max={animation.maxTimestamp}
          value={animation.currentTimestamp}
          onChange={(e) => onTimelineChange(Number(e.target.value))}
          style={{
            background: getTimelineGradient(timeframeInfo, animation.maxTimestamp, animation.currentTimestamp),
          }}
        />
        <div className="anim-time-range">{timeRange}</div>
      </div>

      <div className="anim-right">
        <span className="anim-meta-text">
          {planeCount.toLocaleString()} aircraft
          {timeframeInfo && <span className="highlight"> · {timeframeInfo.planeId}</span>}
        </span>
      </div>
    </div>
  );
};

function getTimelineGradient(
  timeframeInfo?: { start: number; end: number } | null,
  maxTimestamp = 100,
  currentTimestamp = 0
): string {
  const pct = maxTimestamp > 1
    ? ((currentTimestamp - 1) / (maxTimestamp - 1) * 100).toFixed(2)
    : '0';

  if (!timeframeInfo) {
    return `linear-gradient(to right, var(--accent) 0%, var(--accent) ${pct}%, rgba(255,255,255,0.08) ${pct}%, rgba(255,255,255,0.08) 100%)`;
  }

  const s = ((timeframeInfo.start - 1) / maxTimestamp * 100).toFixed(2);
  const e = (timeframeInfo.end / maxTimestamp * 100).toFixed(2);

  return `linear-gradient(to right,
    rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.08) ${s}%,
    var(--amber) ${s}%, var(--amber) ${e}%,
    rgba(255,255,255,0.08) ${e}%, rgba(255,255,255,0.08) 100%)`;
}

export default AnimationControls;
